/**
 * Driver tracking lifecycle diagnostics (diagnostic instrumentation only).
 *
 * Answers, from telemetry alone: did the app recognise EN_ROUTE, start the
 * native watcher, hold permission, run/succeed the seed fix, receive watcher
 * callbacks, get backgrounded, or lose the watcher?
 *
 * PRIVACY: metadata is filtered through an allowlist of lifecycle keys and
 * primitive values. Never pass coordinates, addresses, job/customer ids,
 * tokens or free-text error messages — use `categoriseTrackingError`.
 *
 * Fire-and-forget: never throws, never changes tracking behaviour.
 */
import { Capacitor } from "@capacitor/core";
import { captureEvent } from "@/lib/observability";

export type TrackingDiagEvent =
  | "app_launch"
  | "app_background"
  | "app_foreground"
  | "sign_in"
  | "sign_out"
  | "state_evaluated"
  | "state_changed"
  | "state_flap_suppressed"
  | "poc_signal"
  | "pod_signal"
  | "tracking_requested"
  | "tracking_stopped"
  | "permission_result"
  | "watcher_start_requested"
  | "watcher_started"
  | "watcher_start_failed"
  | "watcher_superseded"
  | "watcher_error"
  | "watcher_stopped"
  | "watcher_restarted"
  | "seed_requested"
  | "seed_succeeded"
  | "seed_failed"
  | "seed_throttled"
  | "location_callback"
  | "location_write_attempted"
  | "location_write_succeeded"
  | "location_write_failed"
  | "recovery_requested"
  | "recovery_succeeded"
  | "recovery_failed"
  | "recovery_stationary"
  | "recovery_watcher_restarted";

const ALLOWED_KEYS = new Set([
  "platform",
  "native",
  "mode",
  "state",
  "previous_state",
  "next_state",
  "reason",
  "driver_found",
  "has_session",
  "active_jobs",
  "assigned_jobs",
  "en_route_jobs",
  "permission",
  "precise",
  "primed",
  "retried_after_prompt",
  "distance_filter_m",
  "error_category",
  "trigger",
  "callbacks_since_start",
  "ms_since_last_callback",
  "ms_since_watcher_start",
  "write_reason",
  "queue_size",
  "consecutive_failures",
  "restart_attempt",
  "outcome",
  "ms_since_last_write",
  "moved_m",
  "at",
]);

export type TrackingErrorCategory =
  | "timeout"
  | "permission_denied"
  | "position_unavailable"
  | "location_services_disabled"
  | "network"
  | "auth"
  | "database"
  | "unknown";

/** Maps any error to a coarse, non-sensitive category (never the raw message). */
export function categoriseTrackingError(err: unknown): TrackingErrorCategory {
  const e = err as { code?: unknown; message?: unknown } | null | undefined;
  const code = String(e?.code ?? "").toUpperCase();
  const msg = String(e?.message ?? err ?? "").toLowerCase();
  if (msg.includes("timeout") || code === "3") return "timeout";
  if (code === "NOT_AUTHORIZED" || code === "1" || msg.includes("denied") || msg.includes("not authorized") || msg.includes("permission"))
    return "permission_denied";
  if (msg.includes("location services") || msg.includes("disabled")) return "location_services_disabled";
  if (code === "2" || msg.includes("unavailable")) return "position_unavailable";
  if (msg.includes("jwt") || msg.includes("auth") || code === "PGRST301") return "auth";
  if (msg.includes("fetch") || msg.includes("network") || msg.includes("load failed")) return "network";
  if (code.startsWith("PGRST") || /^\d{5}$/.test(code)) return "database";
  return "unknown";
}

export function sanitiseTrackingMeta(meta: Record<string, unknown> = {}): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(meta)) {
    if (!ALLOWED_KEYS.has(k)) continue;
    if (v === null || typeof v === "boolean" || typeof v === "number" || typeof v === "string") {
      out[k] = typeof v === "string" ? v.slice(0, 64) : v;
    }
  }
  return out;
}

function platform(): string {
  try {
    return Capacitor.getPlatform();
  } catch {
    return "unknown";
  }
}

const WARN_EVENTS = new Set<TrackingDiagEvent>([
  "watcher_start_failed",
  "watcher_error",
  "seed_failed",
  "location_write_failed",
]);

export function emitTrackingDiag(event: TrackingDiagEvent, meta: Record<string, unknown> = {}): void {
  try {
    captureEvent({
      kind: "native",
      severity: WARN_EVENTS.has(event) ? "warn" : "info",
      component: "tracking.lifecycle",
      message: `tracking.${event}`,
      priority: "high",
      metadata: sanitiseTrackingMeta({
        platform: platform(),
        at: new Date().toISOString(),
        ...meta,
      }),
    });
  } catch {
    /* diagnostics never affect tracking */
  }
}

/**
 * Callback sampler: records the first callback after each watcher start, then
 * at most one summary per interval, so continuous GPS never floods telemetry.
 */
export class LocationCallbackSampler {
  private count = 0;
  private startedAt = 0;
  private lastEmitAt = 0;
  private lastCallbackAt = 0;
  constructor(private readonly intervalMs = 5 * 60_000, private readonly now = () => Date.now()) {}

  watcherStarted(): void {
    this.count = 0;
    this.startedAt = this.now();
    this.lastEmitAt = 0;
    this.lastCallbackAt = 0;
  }

  /** Returns metadata to emit, or null when this callback should not be logged. */
  onCallback(): Record<string, unknown> | null {
    const t = this.now();
    const sinceLast = this.lastCallbackAt ? t - this.lastCallbackAt : null;
    this.count++;
    this.lastCallbackAt = t;
    if (this.count === 1 || t - this.lastEmitAt >= this.intervalMs) {
      this.lastEmitAt = t;
      return {
        trigger: this.count === 1 ? "first_after_start" : "periodic_summary",
        callbacks_since_start: this.count,
        ms_since_watcher_start: this.startedAt ? t - this.startedAt : null,
        ms_since_last_callback: sinceLast,
      };
    }
    return null;
  }
}
